import React, { useEffect, useMemo, useRef, useState } from 'react';
import { shopApi } from '../../utils/shopApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { useShopAdminAuth } from '../../context/ShopAdminAuthContext';
import { connectPrinter, isPrinterConnected, printReceipt, browserPrintFallback } from '../../utils/escposPrinter';
import { ShoppingCart, Search, Trash2, Plus, Minus, Printer, X, CheckCircle2, ScanLine, Wifi, WifiOff, FileText } from 'lucide-react';
import ZReportDialog from './ZReportDialog';

const formatRWF = (n) => new Intl.NumberFormat('en-RW', { maximumFractionDigits: 0 }).format(n || 0);

export default function ShopPosPage() {
  const { hasPerm, staff } = useShopAdminAuth();
  const canDiscount = hasPerm('apply_pos_discount');

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]); // [{product_id, name, unit, unit_price, quantity, stock}]
  const [discountPct, setDiscountPct] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [tendered, setTendered] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [today, setToday] = useState({ count: 0, total_amount: 0 });
  const [scanMode, setScanMode] = useState(false);
  const [printerOn, setPrinterOn] = useState(isPrinterConnected());
  const [zReportOpen, setZReportOpen] = useState(false);
  const searchRef = useRef(null);
  const lastKeyTime = useRef(0);
  const scanBuffer = useRef('');

  const load = () => {
    shopApi.get('/shop/pos/products').then(r => setProducts(r.data)).catch(() => toast.error('Failed to load products'));
    shopApi.get('/shop/pos/sales/today').then(r => setToday(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products.slice(0, 60);
    return products.filter(p => p.name.toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q)).slice(0, 60);
  }, [products, search]);

  const addToCart = (p) => {
    setCart(prev => {
      const idx = prev.findIndex(i => i.product_id === p.id);
      if (idx >= 0) {
        if (prev[idx].quantity + 1 > p.current_stock) { toast.error('Not enough stock'); return prev; }
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + 1 };
        return copy;
      }
      return [...prev, {
        product_id: p.id, name: p.name, unit: p.unit, unit_price: p.pos_price,
        quantity: 1, stock: p.current_stock,
      }];
    });
  };

  /** Barcode-scan handler: scanners emit characters faster than humans (<30ms between keys)
   *  and end with Enter. We detect the rapid burst, look up product by id/barcode/exact name,
   *  add to cart, and clear the search box. */
  const onSearchKeyDown = (e) => {
    const now = Date.now();
    const fast = (now - lastKeyTime.current) < 50;
    lastKeyTime.current = now;
    if (e.key === 'Enter') {
      const code = (scanBuffer.current || search || '').trim();
      scanBuffer.current = '';
      if (!code) return;
      const cleaned = code.toLowerCase();
      const matched = products.find(p =>
        p.id === code ||
        (p.barcode && String(p.barcode).toLowerCase() === cleaned) ||
        p.name.toLowerCase() === cleaned,
      ) || (filtered.length === 1 ? filtered[0] : null);
      if (matched) {
        addToCart(matched);
        toast.success(`Added ${matched.name}`);
        setSearch('');
      } else {
        toast.error('No product matched the scan');
      }
      return;
    }
    if (fast) {
      scanBuffer.current += e.key.length === 1 ? e.key : '';
    } else {
      scanBuffer.current = e.key.length === 1 ? e.key : '';
    }
  };

  // Keep search focused while in scan mode so the scanner's HID keystrokes land here
  useEffect(() => {
    if (scanMode) searchRef.current?.focus();
  }, [scanMode]);

  const changeQty = (product_id, delta) => {
    setCart(prev => prev.flatMap(i => {
      if (i.product_id !== product_id) return [i];
      const next = i.quantity + delta;
      if (next <= 0) return [];
      if (next > i.stock) { toast.error('Not enough stock'); return [i]; }
      return [{ ...i, quantity: next }];
    }));
  };

  const removeLine = (product_id) => setCart(prev => prev.filter(i => i.product_id !== product_id));
  const clearCart = () => { setCart([]); setDiscountPct(0); setTendered(''); setCustomerName(''); setCustomerPhone(''); };

  const subtotal = cart.reduce((s, i) => s + i.unit_price * i.quantity, 0);
  const discountAmount = Math.round(subtotal * ((parseFloat(discountPct) || 0) / 100));
  const total = Math.max(0, Math.round(subtotal - discountAmount));
  const change = paymentMethod === 'cash' && tendered ? Math.max(0, (parseFloat(tendered) || 0) - total) : 0;

  const submitSale = async () => {
    if (cart.length === 0) { toast.error('Add at least one product'); return; }
    setBusy(true);
    try {
      const r = await shopApi.post('/shop/pos/sale', {
        items: cart.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        discount_percent: parseFloat(discountPct) || 0,
        payment_method: paymentMethod,
        customer_name: customerName || 'Walk-in customer',
        customer_phone: customerPhone || '',
        amount_tendered: tendered ? parseFloat(tendered) : null,
      });
      setReceipt(r.data);
      toast.success('Sale recorded');
      clearCart();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record sale');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-serif font-bold flex items-center gap-2"><ShoppingCart className="w-7 h-7 text-primary" />Point of Sale</h1>
          <p className="text-sm text-muted-foreground">Walk-in counter sales · {staff?.name}</p>
        </div>
        <div className="flex items-center gap-2 text-sm flex-wrap" data-testid="pos-today-stats">
          <div className="px-3 py-1.5 rounded-lg bg-primary/10 text-primary"><span className="text-xs text-muted-foreground mr-1">Today's sales:</span><strong>{today.count}</strong></div>
          <div className="px-3 py-1.5 rounded-lg bg-primary/10 text-primary"><span className="text-xs text-muted-foreground mr-1">Total:</span><strong>RWF {formatRWF(today.total_amount)}</strong></div>
          <Button size="sm" variant="outline" onClick={() => setZReportOpen(true)} data-testid="pos-z-report-btn">
            <FileText className="w-4 h-4 mr-1" />Z-Report
          </Button>
          <Button size="sm" variant={scanMode ? 'default' : 'outline'} onClick={() => setScanMode(s => !s)} data-testid="pos-scan-toggle"><ScanLine className="w-4 h-4 mr-1" />Scan {scanMode ? 'ON' : 'OFF'}</Button>
          <Button size="sm" variant="outline" onClick={async () => {
            const r = await connectPrinter();
            if (r.kind === 'serial') { setPrinterOn(true); toast.success('Printer connected'); }
            else if (r.kind === 'unsupported') toast.error(r.error);
            else toast.error(r.error || 'Could not connect');
          }} data-testid="pos-printer-connect">
            {printerOn ? <Wifi className="w-4 h-4 mr-1 text-green-600" /> : <WifiOff className="w-4 h-4 mr-1" />}
            {printerOn ? 'Printer ready' : 'Connect printer'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Product picker */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-border p-4">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input ref={searchRef} placeholder={scanMode ? 'Scan or type then press Enter…' : 'Search products by name or category…'} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={onSearchKeyDown} className="pl-9" autoFocus data-testid="pos-search" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[60vh] overflow-y-auto" data-testid="pos-product-grid">
            {filtered.length === 0 && <div className="col-span-full text-center text-muted-foreground py-10 text-sm">No products match.</div>}
            {filtered.map(p => (
              <button
                key={p.id}
                onClick={() => addToCart(p)}
                className="text-left p-3 rounded-lg border border-border hover:border-primary hover:shadow-sm transition bg-white"
                data-testid={`pos-product-${p.id}`}
              >
                <div className="text-xs text-muted-foreground capitalize">{p.category}</div>
                <div className="font-medium text-sm line-clamp-2">{p.name}</div>
                <div className="flex items-center justify-between mt-1">
                  <div className="text-xs font-semibold text-primary">RWF {formatRWF(p.pos_price)}</div>
                  <div className="text-[10px] text-muted-foreground">{Math.floor(p.current_stock)} {p.unit}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Cart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-border p-4 flex flex-col" data-testid="pos-cart">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold">Current sale</h2>
            {cart.length > 0 && <Button size="sm" variant="ghost" onClick={clearCart} data-testid="pos-clear-cart"><X className="w-4 h-4 mr-1" />Clear</Button>}
          </div>

          <div className="flex-1 overflow-y-auto max-h-[40vh] divide-y divide-border">
            {cart.length === 0 ? (
              <div className="text-center text-muted-foreground text-sm py-10">No items yet. Click a product to add it.</div>
            ) : cart.map(line => (
              <div key={line.product_id} className="py-2 flex items-center gap-2" data-testid={`pos-cart-line-${line.product_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{line.name}</div>
                  <div className="text-xs text-muted-foreground">RWF {formatRWF(line.unit_price)} / {line.unit}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => changeQty(line.product_id, -1)} data-testid={`pos-qty-minus-${line.product_id}`}><Minus className="w-3 h-3" /></Button>
                  <span className="text-sm w-7 text-center">{line.quantity}</span>
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => changeQty(line.product_id, 1)} data-testid={`pos-qty-plus-${line.product_id}`}><Plus className="w-3 h-3" /></Button>
                </div>
                <div className="w-20 text-right text-sm font-semibold">RWF {formatRWF(line.unit_price * line.quantity)}</div>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeLine(line.product_id)} data-testid={`pos-remove-${line.product_id}`}><Trash2 className="w-3 h-3 text-red-500" /></Button>
              </div>
            ))}
          </div>

          <div className="border-t pt-3 mt-2 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="pos-subtotal">RWF {formatRWF(subtotal)}</span></div>
            <div className="flex items-center gap-2">
              <Label className="text-xs w-20">Discount %</Label>
              <Input type="number" min="0" max="90" value={discountPct} disabled={!canDiscount} onChange={(e) => setDiscountPct(e.target.value)} className="h-8" data-testid="pos-discount" />
              {!canDiscount && <span className="text-[10px] text-muted-foreground">Supervisor only</span>}
            </div>
            {discountAmount > 0 && <div className="flex justify-between text-green-700"><span>Discount</span><span>- RWF {formatRWF(discountAmount)}</span></div>}
            <div className="flex justify-between text-lg font-bold border-t pt-2"><span>Total</span><span data-testid="pos-total">RWF {formatRWF(total)}</span></div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <div>
                <Label className="text-xs">Payment</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="h-9" data-testid="pos-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="momo">Mobile Money</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {paymentMethod === 'cash' && (
                <div>
                  <Label className="text-xs">Tendered (RWF)</Label>
                  <Input type="number" value={tendered} onChange={(e) => setTendered(e.target.value)} className="h-9" data-testid="pos-tendered" />
                </div>
              )}
            </div>
            {paymentMethod === 'cash' && tendered && (
              <div className="flex justify-between text-sm font-medium"><span>Change</span><span data-testid="pos-change">RWF {formatRWF(change)}</span></div>
            )}

            <div className="grid grid-cols-2 gap-2 pt-1">
              <Input placeholder="Customer name (opt)" value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="h-9" data-testid="pos-customer-name" />
              <Input placeholder="Phone (opt)" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} className="h-9" data-testid="pos-customer-phone" />
            </div>

            <Button onClick={submitSale} disabled={busy || cart.length === 0} className="w-full mt-2 h-11 text-base" data-testid="pos-submit">
              {busy ? 'Recording…' : `Charge RWF ${formatRWF(total)}`}
            </Button>
          </div>
        </div>
      </div>

      {/* Receipt dialog */}
      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="max-w-sm" data-testid="pos-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-green-600" />Sale complete</DialogTitle>
            <DialogDescription>Receipt summary. Print to your thermal printer or hand a copy to the customer.</DialogDescription>
          </DialogHeader>
          {receipt && (
            <div className="space-y-3 text-sm font-mono">
              <div className="text-center">
                <div className="font-bold font-serif">Bison Shop</div>
                <div className="text-xs text-muted-foreground">Receipt #{receipt.order.order_number}</div>
                <div className="text-xs text-muted-foreground">{new Date(receipt.order.created_at).toLocaleString()}</div>
              </div>
              <div className="border-t border-dashed pt-2 space-y-1">
                {receipt.order.items.map(it => (
                  <div key={it.product_id} className="flex justify-between text-xs">
                    <span className="truncate flex-1">{it.product_name} × {it.quantity}</span>
                    <span>RWF {formatRWF(it.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-dashed pt-2 space-y-0.5">
                <div className="flex justify-between"><span>Subtotal</span><span>RWF {formatRWF(receipt.order.subtotal)}</span></div>
                {receipt.order.discount > 0 && <div className="flex justify-between text-green-700"><span>Discount</span><span>- RWF {formatRWF(receipt.order.discount)}</span></div>}
                <div className="flex justify-between font-bold text-base border-t pt-1"><span>TOTAL</span><span>RWF {formatRWF(receipt.order.total)}</span></div>
                <div className="flex justify-between text-xs"><span>Paid by</span><span className="capitalize">{receipt.order.payment_method.replace(/_/g, ' ')}</span></div>
                {receipt.change !== null && receipt.change !== undefined && (
                  <div className="flex justify-between text-xs font-bold"><span>Change</span><span>RWF {formatRWF(receipt.change)}</span></div>
                )}
              </div>
              <div className="text-center text-xs text-muted-foreground">Served by {receipt.order.pos_cashier_name}</div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={async () => {
                  if (printerOn) {
                    try { await printReceipt({ ...receipt.order, change: receipt.change }); toast.success('Sent to thermal printer'); }
                    catch (e) { toast.error(e.message); browserPrintFallback(); }
                  } else {
                    browserPrintFallback();
                  }
                }} data-testid="pos-print-receipt"><Printer className="w-4 h-4 mr-1" />Print</Button>
                <Button className="flex-1" onClick={() => setReceipt(null)} data-testid="pos-receipt-close">New sale</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ZReportDialog open={zReportOpen} onOpenChange={setZReportOpen} isAdmin={staff?.role === 'admin'} />
    </div>
  );
}
